import {
  and,
  asc,
  count,
  countDistinct,
  desc,
  eq,
  gt,
  inArray,
  isNotNull,
  isNull,
  lt,
  lte,
  ne,
  notInArray,
  or,
  sql,
} from 'drizzle-orm'
import { alias } from 'drizzle-orm/pg-core'

import type { Database } from '../client'
import type { SeatEntitlementStatus, SeatSourceType, SelectPremium, SelectSeatEntitlement } from '../schema'
import type { DatabaseTransaction } from './child-merge'
import {
  isGrantFamily,
  member,
  Membership,
  MembershipAddOn,
  organization,
  Premium,
  SeatEntitlement,
  user,
} from '../schema'

/**
 * Seat reads for the pool model.
 *
 * A POOL is one {@link Membership} row plus its pro-rata {@link MembershipAddOn} rows. Every seat in
 * a pool shares the pool's window. An organization may hold several pools at once with different end
 * dates — 50 purchased seats to December alongside a 20-seat scholarship to March.
 *
 * Three organization-level numbers live here and must never be mixed:
 * - **Owned seats** — what the organization provisioned. Drives the seat ledger, billing, renewal.
 * - **Reach** — children in the organization holding an active seat from ANY organization, a
 *   parent's family subscription included. Answers "who can read premium right now".
 * - **Capacity** — owned seats (assigned or not) plus children covered from elsewhere. Drives the
 *   school feature gates and the "x/50" locks, so a school gets credit for seats it holds but has
 *   not handed out yet.
 */

/** Where a pool came from. `entitlement` covers both grants and offline-paid `direct_paid`. */
export type SeatPoolSource = 'purchase' | 'entitlement'

export interface SeatPool {
  /** The carrying `Membership` id. Renewal and seat-reset both address a pool by this. */
  id: string
  organizationId: string
  source: SeatPoolSource
  /** The entitlement's flavour when entitlement-backed, else `null`. */
  sourceType: SeatSourceType | null
  /**
   * False only for a grant-family pool (scholarship / free trial / pilot / internal / pemda).
   * A plain purchase and an offline-paid `direct_paid` entitlement are both commercial.
   */
  isCommercial: boolean
  entitlementId: string | null
  invoiceId: string | null
  /**
   * True when the pool has not started yet — `startDate > now`.
   *
   * A renewal buys a pool that begins the day the current one ends, so between payment and that
   * date the organization holds seats nobody can use yet. Always false unless the read was asked
   * for scheduled pools; every other caller sees live pools only.
   */
  isScheduled: boolean
  startDate: Date
  endDate: Date
  /** Live seats in the pool — the membership's own plus its pro-rata add-ons'. */
  seatsTotal: number
  seatsUsed: number
  seatsAvailable: number
  /** Pro-rata add-ons riding this pool. They share its window and renew with it. */
  addOnIds: string[]
}

/**
 * Every seat pool the organization holds, soonest-expiring first.
 *
 * Active means `startDate <= now < endDate`. Pass `includeExpired` to get the full history, which
 * admin surfaces need and the app does not.
 *
 * `includeScheduled` additionally returns pools that have not started yet. Those are what a renewal
 * mints — it buys a pool starting the day the current one ends — and they must stay OUT of the
 * default read: a scheduled pool's seats are real rows with a real `endDate`, so a caller summing
 * `seatsTotal` would count seats nobody can sit in yet, and a premium gate would unlock early.
 */
export async function getOrgSeatPools(
  db: Database,
  organizationId: string,
  options: { includeExpired?: boolean; includeScheduled?: boolean } = {},
): Promise<SeatPool[]> {
  const now = new Date()

  const windowFilter = options.includeExpired
    ? undefined
    : options.includeScheduled
      ? gt(Membership.endDate, now)
      : and(lte(Membership.startDate, now), gt(Membership.endDate, now))

  const memberships = await db.query.Membership.findMany({
    where: and(eq(Membership.organizationId, organizationId), windowFilter),
    with: { addOns: { columns: { id: true } } },
    // `id` breaks ties: two pools bought in the same term share an end date, and ordering by date
    // alone leaves their relative order to Postgres — it can differ between calls, which silently
    // moves which pool a caller reading `pools[0]` is looking at.
    orderBy: [asc(Membership.endDate), asc(Membership.id)],
  })
  if (memberships.length === 0) return []

  const membershipIds = memberships.map((m) => m.id)
  const addOnToMembership = new Map<string, string>()
  for (const m of memberships) {
    for (const addOn of m.addOns) addOnToMembership.set(addOn.id, m.id)
  }
  const addOnIds = [...addOnToMembership.keys()]

  // Seats are counted from `premium`, never from `membership.seats`: that column records what was
  // bought and knows nothing about add-on seats or seats released since.
  const seatRows = await db
    .select({
      membershipId: Premium.membershipId,
      membershipAddOnId: Premium.membershipAddOnId,
      entitlementId: Premium.entitlementId,
      childId: Premium.childId,
    })
    .from(Premium)
    .where(
      and(
        gt(Premium.endDate, now),
        or(
          inArray(Premium.membershipId, membershipIds),
          addOnIds.length > 0 ? inArray(Premium.membershipAddOnId, addOnIds) : sql`false`,
        ),
      ),
    )

  interface Tally {
    total: number
    used: number
    entitlementId: string | null
  }
  const tallies = new Map<string, Tally>()

  for (const row of seatRows) {
    // An add-on seat belongs to the pool its add-on rides, not to a pool of its own.
    const poolId = row.membershipAddOnId ? addOnToMembership.get(row.membershipAddOnId) : row.membershipId
    if (!poolId) continue

    const tally = tallies.get(poolId) ?? { total: 0, used: 0, entitlementId: null }
    tally.total += 1
    if (row.childId !== null) tally.used += 1
    // Entitlement-provisioned seats carry both ids, so the pool inherits the tag from its seats —
    // there is no direct membership -> entitlement foreign key.
    if (row.entitlementId) tally.entitlementId = row.entitlementId
    tallies.set(poolId, tally)
  }

  const entitlementIds = [...new Set([...tallies.values()].map((t) => t.entitlementId).filter((id) => id !== null))]
  const sourceTypeById = new Map<string, SeatSourceType>()
  if (entitlementIds.length > 0) {
    const entitlements = await db.query.SeatEntitlement.findMany({
      where: inArray(SeatEntitlement.id, entitlementIds),
      columns: { id: true, sourceType: true },
    })
    for (const e of entitlements) sourceTypeById.set(e.id, e.sourceType)
  }

  return memberships.map((m) => {
    const tally = tallies.get(m.id) ?? { total: 0, used: 0, entitlementId: null }
    const sourceType = tally.entitlementId ? (sourceTypeById.get(tally.entitlementId) ?? null) : null

    return {
      id: m.id,
      organizationId: m.organizationId,
      source: tally.entitlementId ? 'entitlement' : 'purchase',
      sourceType,
      isCommercial: sourceType === null || !isGrantFamily(sourceType),
      entitlementId: tally.entitlementId,
      invoiceId: m.invoiceId,
      isScheduled: m.startDate > now,
      startDate: m.startDate,
      endDate: m.endDate,
      seatsTotal: tally.total,
      seatsUsed: tally.used,
      seatsAvailable: tally.total - tally.used,
      addOnIds: m.addOns.map((a) => a.id),
    }
  })
}

/** What a {@link SeatGroup} draws its seats from. */
export type SeatGroupKind = 'membership' | 'add-on'

/**
 * A timestamp reduced to its calendar day, for grouping windows that READ the same.
 *
 * Add-on segments start at the moment the invoice was paid, so two top-ups placed the same morning
 * carry start dates minutes apart. Keyed to the millisecond they never merge, while the picker
 * prints both as "20 Sep 2026" — which is exactly the pair of identical-looking rows the merge
 * exists to remove. Day granularity is the granularity the label itself has.
 */
function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

/**
 * One block of seats an admin can hand out, named by the window those seats actually cover.
 *
 * NOT the same unit as a {@link SeatPool}. A pool folds its pro-rata add-ons into the membership
 * they ride, which is the right answer for billing — one pool, one renewal, one invoice chain — and
 * the wrong one for the assignment dialog. Add-on seats are bought mid-term and carry their own
 * `startDate`, so a pool built on 1 Sep and topped up on 18 Sep holds two windows under one name.
 * Folded in, those seats surfaced under a row reading "Mulai 1 Sep 2027" — accurate about that
 * pool, and wrong about the purchase, which also bought seats usable the day it was paid for.
 *
 * AN ADD-ON GROUP IS A WHOLE PURCHASE, NOT ONE `MembershipAddOn` ROW. `mintAddOnSeats` writes one
 * add-on per paid pool under a single `invoiceId`, and their windows are contiguous — a top-up on
 * 18 Sep against a subscription already renewed to 2028 writes 18 Sep 2026 -> 1 Sep 2027 and
 * 1 Sep 2027 -> 1 Sep 2028. The buyer bought ONE thing, and the checkout told them so
 * ("Kursi aktif sampai 1 Sep 2028"). Listing the rows separately would advertise twice the seats
 * that were sold and let an admin fill only the first segment, leaving the child uncovered from
 * 2027 — exactly the cover the invoice already charged for. So the segments are chained: one group,
 * one seat count, `startDate` the earliest and `endDate` the latest, and a handout stamps the child
 * onto one seat in EVERY segment.
 */
export interface SeatGroup {
  /**
   * Stable identity for a picker, and what the handout mutation is scoped by.
   *
   * The membership id for a membership group; the ADD-ON'S INVOICE id for an add-on group, because
   * the group is the purchase rather than any one of the rows it minted.
   */
  key: string
  kind: SeatGroupKind
  /**
   * The carrying membership.
   *
   * On an add-on group this is the FIRST segment's pool — the one the purchase joined on the day it
   * was paid for. Present so a caller has a pool to name; the seats themselves span several.
   */
  membershipId: string
  /** Every `MembershipAddOn` segment behind this group, earliest first. Empty on a membership group. */
  addOnIds: string[]
  /**
   * The add-on purchases this group covers. Empty on a membership group.
   *
   * Usually one. SEVERAL when a school placed more than one top-up over the same window — two
   * five-seat orders on the same day against the same pools are two invoices and one thing to
   * choose from, so they are merged rather than listed as identical rows an admin has to pick
   * between blind. The handout spends them in turn; see `giveChildrenPremium`.
   */
  addOnInvoiceIds: string[]
  /** The entitlement's flavour when entitlement-backed, else `null`. Inherited from the pool. */
  sourceType: SeatSourceType | null
  /** Inherited from the pool: an add-on can only ride a pool the organization pays for. */
  isCommercial: boolean
  /** True when these seats have not started yet — `startDate > now`. */
  isScheduled: boolean
  startDate: Date
  endDate: Date
  /**
   * Seats SOLD, not seat rows.
   *
   * An add-on purchase mints `seats` rows per segment, so summing the rows would report a five-seat
   * top-up as ten. The widest segment is the size of the purchase.
   */
  seatsTotal: number
  /** Distinct children holding a seat in any segment. */
  seatsUsed: number
  /**
   * How many more children this group can cover FOR ITS WHOLE WINDOW.
   *
   * The tightest segment, not the sum: covering a child to the end takes one seat in each, so a
   * chain with three free seats in one segment and one in the next can still only take one child.
   */
  seatsAvailable: number
}

/**
 * Every block of seats the organization can assign from, split by window — see {@link SeatGroup}.
 *
 * Ordered soonest-starting first, then soonest-ending, with the id breaking ties so two calls
 * cannot disagree about which group a caller reading by index is looking at.
 *
 * `includeScheduled` carries the same meaning and the same warning as it does on
 * {@link getOrgSeatPools}: seats that have not opened yet are real rows, and a caller summing them
 * as availability would report access nobody has today.
 *
 * Groups with no seat rows at all are dropped. An add-on whose seats have all expired, or a
 * membership whose every seat is an add-on seat, has nothing to offer and would only read as an
 * empty row in a picker.
 */
export async function getOrgSeatGroups(
  db: Database,
  organizationId: string,
  options: { includeScheduled?: boolean } = {},
): Promise<SeatGroup[]> {
  const now = new Date()

  const windowFilter = options.includeScheduled
    ? gt(Membership.endDate, now)
    : and(lte(Membership.startDate, now), gt(Membership.endDate, now))

  const memberships = await db.query.Membership.findMany({
    where: and(eq(Membership.organizationId, organizationId), windowFilter),
    with: { addOns: { columns: { id: true, invoiceId: true, startDate: true, endDate: true } } },
    orderBy: [asc(Membership.endDate), asc(Membership.id)],
  })
  if (memberships.length === 0) return []

  const membershipIds = memberships.map((m) => m.id)
  const addOnToMembership = new Map<string, string>()
  for (const m of memberships) {
    for (const addOn of m.addOns) addOnToMembership.set(addOn.id, m.id)
  }
  const addOnIds = [...addOnToMembership.keys()]

  const seatRows = await db
    .select({
      membershipId: Premium.membershipId,
      membershipAddOnId: Premium.membershipAddOnId,
      entitlementId: Premium.entitlementId,
      childId: Premium.childId,
    })
    .from(Premium)
    .where(
      and(
        gt(Premium.endDate, now),
        or(
          inArray(Premium.membershipId, membershipIds),
          addOnIds.length > 0 ? inArray(Premium.membershipAddOnId, addOnIds) : sql`false`,
        ),
      ),
    )

  interface Tally {
    total: number
    used: number
    children: Set<string>
    entitlementId: string | null
  }
  const emptyTally = (): Tally => ({ total: 0, used: 0, children: new Set(), entitlementId: null })

  /** Per SEGMENT: keyed by add-on id for add-on seats, by membership id for a membership's own. */
  const tallies = new Map<string, Tally>()
  /** Pool-level flavour, which an add-on group inherits — its own seats carry no entitlement id. */
  const poolEntitlementId = new Map<string, string>()

  for (const row of seatRows) {
    const key = row.membershipAddOnId ?? row.membershipId
    if (!key) continue

    const tally = tallies.get(key) ?? emptyTally()
    tally.total += 1
    if (row.childId !== null) {
      tally.used += 1
      tally.children.add(row.childId)
    }
    if (row.entitlementId) tally.entitlementId = row.entitlementId
    tallies.set(key, tally)

    const poolId = row.membershipAddOnId ? addOnToMembership.get(row.membershipAddOnId) : row.membershipId
    if (poolId && row.entitlementId) poolEntitlementId.set(poolId, row.entitlementId)
  }

  const entitlementIds = [...new Set(poolEntitlementId.values())]
  const sourceTypeById = new Map<string, SeatSourceType>()
  if (entitlementIds.length > 0) {
    const entitlements = await db.query.SeatEntitlement.findMany({
      where: inArray(SeatEntitlement.id, entitlementIds),
      columns: { id: true, sourceType: true },
    })
    for (const e of entitlements) sourceTypeById.set(e.id, e.sourceType)
  }

  const groups: SeatGroup[] = []

  /**
   * Add-on segments collected across EVERY pool, keyed by the invoice that bought them.
   *
   * Across pools, not within one: that is the whole point. A top-up placed after a renewal writes a
   * segment on the live pool and another on the scheduled one, and they only read as one purchase
   * when both pools have been walked.
   */
  interface Chain {
    invoiceId: string
    membershipId: string
    addOnIds: string[]
    sourceType: SeatSourceType | null
    isCommercial: boolean
    startDate: Date
    endDate: Date
    seatsTotal: number
    seatsAvailable: number
    children: Set<string>
    /** False once any segment turns out to hold no live seat row — see the filter below. */
    hasSeats: boolean
  }
  const chains = new Map<string, Chain>()

  for (const m of memberships) {
    const entitlementId = poolEntitlementId.get(m.id) ?? null
    const sourceType = entitlementId ? (sourceTypeById.get(entitlementId) ?? null) : null
    const isCommercial = sourceType === null || !isGrantFamily(sourceType)

    const own = tallies.get(m.id)
    if (own && own.total > 0) {
      groups.push({
        key: m.id,
        kind: 'membership',
        membershipId: m.id,
        addOnIds: [],
        addOnInvoiceIds: [],
        sourceType,
        isCommercial,
        isScheduled: m.startDate > now,
        startDate: m.startDate,
        endDate: m.endDate,
        seatsTotal: own.total,
        seatsUsed: own.used,
        seatsAvailable: own.total - own.used,
      })
    }

    for (const addOn of m.addOns) {
      const tally = tallies.get(addOn.id)
      const segmentTotal = tally?.total ?? 0
      const segmentAvailable = segmentTotal - (tally?.used ?? 0)

      const held = chains.get(addOn.invoiceId)
      if (!held) {
        chains.set(addOn.invoiceId, {
          invoiceId: addOn.invoiceId,
          membershipId: m.id,
          addOnIds: [addOn.id],
          sourceType,
          isCommercial,
          startDate: addOn.startDate,
          endDate: addOn.endDate,
          seatsTotal: segmentTotal,
          seatsAvailable: segmentAvailable,
          children: new Set(tally?.children ?? []),
          hasSeats: segmentTotal > 0,
        })
        continue
      }

      held.addOnIds.push(addOn.id)
      if (addOn.startDate < held.startDate) {
        held.startDate = addOn.startDate
        // The earliest segment names the pool: it is the one the purchase joined when it was paid.
        held.membershipId = m.id
      }
      if (addOn.endDate > held.endDate) held.endDate = addOn.endDate
      // Widest segment is the size of the purchase; tightest is what can still be covered end to end.
      held.seatsTotal = Math.max(held.seatsTotal, segmentTotal)
      held.seatsAvailable = Math.min(held.seatsAvailable, segmentAvailable)
      for (const childId of tally?.children ?? []) held.children.add(childId)
      // A chain is only offerable while EVERY segment still has rows. One expired segment means the
      // window is no longer continuous, and a handout could not cover the child to `endDate`.
      if (segmentTotal === 0) held.hasSeats = false
    }
  }

  /**
   * Chains that cover exactly the same window are ONE row.
   *
   * Two top-ups bought on the same day against the same pools produce two invoices with identical
   * windows, and listing them separately gave an admin two rows reading "Kursi tambahan · 20 Sep
   * 2026 – 20 Sep 2028" with no way to tell them apart or any reason to care which they picked.
   *
   * Capacity ADDS here, unlike within a chain: each purchase covers its own children end to end, so
   * two five-seat orders seat ten. `seatsUsed` is the union — a child can only hold one of them.
   */
  const byWindow = new Map<string, Chain[]>()
  for (const chain of chains.values()) {
    if (!chain.hasSeats) continue

    /**
     * Same days AND the same answer to "can this be spent today".
     *
     * `isScheduled` is in the key because it is the one difference a shared day could still hide: a
     * chain that opened this morning and one that opens later today print the same date and behave
     * differently — seats from the first work now, seats from the second leave the student Terbatas
     * until they open. In practice a school cannot reach that state, since a top-up starts at the
     * moment it is paid for; it is in the key so the merge can never quietly claim otherwise.
     */
    const windowKey = `${dayKey(chain.startDate)}|${dayKey(chain.endDate)}|${chain.startDate > now ? 'scheduled' : 'live'}`
    const held = byWindow.get(windowKey)
    if (held) held.push(chain)
    else byWindow.set(windowKey, [chain])
  }

  for (const merged of byWindow.values()) {
    // Ordered so `key` and the id lists are stable between calls — two chains in a map have no
    // inherent order, and a picker reading `key` must not see it change under a refetch.
    merged.sort((a, b) => a.invoiceId.localeCompare(b.invoiceId))

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const first = merged[0]!
    const children = new Set<string>()
    for (const chain of merged) for (const childId of chain.children) children.add(childId)

    // The true extremes, not the first chain's: merged windows agree on the DAY, and can still
    // differ by the hours between two payments.
    const startDate = merged.reduce(
      (earliest, chain) => (chain.startDate < earliest ? chain.startDate : earliest),
      first.startDate,
    )
    const endDate = merged.reduce((latest, chain) => (chain.endDate > latest ? chain.endDate : latest), first.endDate)

    groups.push({
      key: merged.map((chain) => chain.invoiceId).join('+'),
      kind: 'add-on',
      membershipId: first.membershipId,
      addOnIds: merged.flatMap((chain) => chain.addOnIds),
      addOnInvoiceIds: merged.map((chain) => chain.invoiceId),
      sourceType: first.sourceType,
      isCommercial: first.isCommercial,
      isScheduled: startDate > now,
      startDate,
      endDate,
      seatsTotal: merged.reduce((sum, chain) => sum + chain.seatsTotal, 0),
      seatsUsed: children.size,
      seatsAvailable: merged.reduce((sum, chain) => sum + Math.max(0, chain.seatsAvailable), 0),
    })
  }

  return groups.sort(
    (a, b) =>
      a.startDate.getTime() - b.startDate.getTime() ||
      a.endDate.getTime() - b.endDate.getTime() ||
      a.key.localeCompare(b.key),
  )
}

/**
 * Unassigned seats in ONE add-on segment, soonest-expiring first.
 *
 * The add-on sibling of {@link getAvailablePoolSeats}, which spends a membership's seats and its
 * add-ons' together. Covering a child from an add-on PURCHASE means taking one of these from every
 * segment of the chain — see `getOrgSeatGroups` — so the caller walks the chain and calls this per
 * segment rather than asking for a number of seats across it.
 */
export async function getAvailableAddOnSeats(
  db: Database,
  organizationId: string,
  addOnId: string,
  limit?: number,
  options: { includeScheduled?: boolean } = {},
): Promise<SelectPremium[]> {
  const now = new Date()

  return db.query.Premium.findMany({
    where: and(
      eq(Premium.organizationId, organizationId),
      eq(Premium.membershipAddOnId, addOnId),
      options.includeScheduled ? undefined : lte(Premium.startDate, now),
      gt(Premium.endDate, now),
      isNull(Premium.childId),
    ),
    orderBy: [asc(Premium.endDate), asc(Premium.id)],
    limit,
  })
}

/** One add-on purchase's segments, earliest first — what a chain handout has to fill. */
export interface AddOnChainSegment {
  id: string
  startDate: Date
  endDate: Date
}

/**
 * Every segment of the add-on purchase `invoiceId` bought for this organization, earliest first.
 *
 * Bounded by `organizationId` so an invoice belonging to another school finds nothing rather than
 * that school's segments, and by `endDate > now` so a chain with an elapsed segment cannot be
 * spent — the window it advertises would no longer be continuous.
 */
export async function getAddOnChainSegments(
  db: Database,
  organizationId: string,
  invoiceId: string,
): Promise<AddOnChainSegment[]> {
  const now = new Date()

  return db
    .select({
      id: MembershipAddOn.id,
      startDate: MembershipAddOn.startDate,
      endDate: MembershipAddOn.endDate,
    })
    .from(MembershipAddOn)
    .where(
      and(
        eq(MembershipAddOn.invoiceId, invoiceId),
        eq(MembershipAddOn.organizationId, organizationId),
        gt(MembershipAddOn.endDate, now),
      ),
    )
    .orderBy(asc(MembershipAddOn.startDate), asc(MembershipAddOn.id))
}

/** One pool the organization has paid for that has not started yet. */
export interface ScheduledSeatPool {
  /** The carrying `Membership` id. */
  id: string
  startDate: Date
  endDate: Date
  /** Seats in the pool — assigned and unassigned together. */
  seatsTotal: number
  /**
   * Seats with a child already stamped on: a renewal's continuing students, seated at checkout.
   *
   * These need no announcement — the roster already shows the child covered through the new window
   * — which is why the "akan aktif" notice counts only `seatsUnassigned`.
   */
  seatsAssigned: number
  /** Seats nobody holds yet. The number worth telling an admin about. */
  seatsUnassigned: number
}

/**
 * Pools the organization has bought that start in the FUTURE, soonest-starting first.
 *
 * A renewal mints one of these: the new term begins the day the current pool ends, so the seats
 * exist, are paid for, and are unusable until then. Every seat read in this file is windowed, so
 * they are invisible everywhere else by construction — which is exactly why they need a surface of
 * their own, or a school that has already paid sees no sign of it until the day it activates.
 */
export async function getScheduledSeatPools(db: Database, organizationId: string): Promise<ScheduledSeatPool[]> {
  const now = new Date()

  const memberships = await db.query.Membership.findMany({
    where: and(
      eq(Membership.organizationId, organizationId),
      gt(Membership.startDate, now),
      gt(Membership.endDate, now),
    ),
    columns: { id: true, startDate: true, endDate: true },
    orderBy: [asc(Membership.startDate), asc(Membership.id)],
  })

  if (memberships.length === 0) return []

  /**
   * Counted from `premium`, never from `membership.seats`, for the same reason the live read is:
   * that column records what was bought, and a seat released between payment and activation
   * (a student leaving the school) would keep being counted.
   *
   * Add-on seats included ONLY when they are actually waiting. A top-up joins every pool the school
   * has already paid for, so a purchase that rides a live pool today and the scheduled one after it
   * writes a segment on each — and its seats are usable NOW, through the live segment. Counting the
   * scheduled segment would announce "3 kursi akan aktif 1 Sep 2027" for seats an admin can hand
   * out this afternoon. This is exactly the shape a renewal's empty seats take (they bridge from
   * payment to the new pool's end), so the notice would misreport every such renewal. A chain is
   * "waiting" only when NO segment of it is live.
   */
  const membershipIds = memberships.map((m) => m.id)

  const addOns = await db
    .select({
      id: MembershipAddOn.id,
      membershipId: MembershipAddOn.membershipId,
      invoiceId: MembershipAddOn.invoiceId,
    })
    .from(MembershipAddOn)
    .where(inArray(MembershipAddOn.membershipId, membershipIds))

  const chainInvoiceIds = [...new Set(addOns.map((addOn) => addOn.invoiceId))]

  const liveChains =
    chainInvoiceIds.length > 0
      ? await db
          .select({ invoiceId: MembershipAddOn.invoiceId })
          .from(MembershipAddOn)
          .where(
            and(
              inArray(MembershipAddOn.invoiceId, chainInvoiceIds),
              lte(MembershipAddOn.startDate, now),
              gt(MembershipAddOn.endDate, now),
            ),
          )
      : []

  const liveChainInvoiceIds = new Set(liveChains.map((row) => row.invoiceId))

  const addOnToMembership = new Map(
    addOns.filter((addOn) => !liveChainInvoiceIds.has(addOn.invoiceId)).map((addOn) => [addOn.id, addOn.membershipId]),
  )
  const addOnIds = [...addOnToMembership.keys()]

  const seatRows = await db
    .select({
      membershipId: Premium.membershipId,
      membershipAddOnId: Premium.membershipAddOnId,
      childId: Premium.childId,
    })
    .from(Premium)
    .where(
      and(
        gt(Premium.endDate, now),
        or(
          inArray(Premium.membershipId, membershipIds),
          addOnIds.length > 0 ? inArray(Premium.membershipAddOnId, addOnIds) : sql`false`,
        ),
      ),
    )

  const tallies = new Map<string, { total: number; assigned: number }>()

  for (const row of seatRows) {
    // An add-on seat belongs to the pool its add-on rides, not to a pool of its own.
    const poolId = row.membershipAddOnId ? addOnToMembership.get(row.membershipAddOnId) : row.membershipId
    if (!poolId) continue

    const tally = tallies.get(poolId) ?? { total: 0, assigned: 0 }
    tally.total += 1
    if (row.childId !== null) tally.assigned += 1
    tallies.set(poolId, tally)
  }

  return memberships.map((m) => {
    const tally = tallies.get(m.id) ?? { total: 0, assigned: 0 }

    return {
      id: m.id,
      startDate: m.startDate,
      endDate: m.endDate,
      seatsTotal: tally.total,
      seatsAssigned: tally.assigned,
      seatsUnassigned: tally.total - tally.assigned,
    }
  })
}

/**
 * Every live seat the organization owns, across all its pools.
 *
 * Replaces the old `getActiveMembershipPremiums`, which picked ONE membership by `desc(endDate)` and
 * returned only its seats — so a school holding two pools saw one of them and the other's seats were
 * invisible and unassignable.
 */
export async function getActiveOrgSeats(db: Database, organizationId: string): Promise<SelectPremium[]> {
  const now = new Date()
  return db.query.Premium.findMany({
    where: and(eq(Premium.organizationId, organizationId), gt(Premium.endDate, now)),
    // `id` breaks ties. Seats in one pool share an end date, so ordering by date alone leaves their
    // relative order up to Postgres and it can differ between calls, which matters because two
    // concurrent handouts must walk the same order. Nothing here resolves a CHILD's seat any more —
    // that is `findCoveringSeatForChild`, because a child's seat need not belong to the organization
    // whose roster is being read.
    orderBy: [asc(Premium.endDate), asc(Premium.id)],
  })
}

/**
 * Unassigned live seats the organization can hand out, soonest-expiring FIRST.
 *
 * The ordering is the seat-handout policy: a seat closing sooner is spent before one closing later,
 * so a school gets the full value of a grant that expires ahead of its purchased seats.
 */
export async function getAvailableOrgSeats(
  db: Database,
  organizationId: string,
  limit?: number,
): Promise<SelectPremium[]> {
  const now = new Date()
  return db.query.Premium.findMany({
    // Bounded on BOTH ends. A renewal mints seats that start when the current pool closes, and
    // handing one of those out today assigns a child a seat that does nothing until then — the
    // roster would show them Terbatas while the school believes it has seated them.
    where: and(
      eq(Premium.organizationId, organizationId),
      lte(Premium.startDate, now),
      gt(Premium.endDate, now),
      sql`${Premium.childId} IS NULL`,
    ),
    // `id` breaks ties so two concurrent grabs walk the same order.
    orderBy: [asc(Premium.endDate), asc(Premium.id)],
    limit,
  })
}

/**
 * Unassigned live seats belonging to ONE pool, soonest-expiring first.
 *
 * A pool's seats hang off two different columns — the carrying membership's own on
 * `membershipId`, its pro-rata add-ons' on `membershipAddOnId`, and a seat carries exactly one of
 * the two — so scoping by `membershipId` alone would hand out only part of the pool.
 *
 * `organizationId` still bounds the read, so a membership id belonging to some other organization
 * yields nothing rather than that organization's seats.
 */
export async function getAvailablePoolSeats(
  db: Database,
  organizationId: string,
  membershipId: string,
  limit?: number,
  options: { includeScheduled?: boolean; excludeAddOns?: boolean } = {},
): Promise<SelectPremium[]> {
  const now = new Date()

  /**
   * `excludeAddOns` narrows this to the membership's OWN seats.
   *
   * The default union is what a POOL means — membership plus the add-ons that renew with it. A
   * caller working in {@link SeatGroup} units has already split those apart and is showing the two
   * counts separately, so spending an add-on seat against a membership group would hand out a seat
   * from a window the admin was not looking at. See `getOrgSeatGroups`.
   */
  const addOns = options.excludeAddOns
    ? []
    : await db
        .select({ id: MembershipAddOn.id })
        .from(MembershipAddOn)
        .where(eq(MembershipAddOn.membershipId, membershipId))
  const addOnIds = addOns.map((addOn) => addOn.id)

  return db.query.Premium.findMany({
    where: and(
      eq(Premium.organizationId, organizationId),
      /**
       * Live seats only by default, for the reason `getAvailableOrgSeats` spells out: handing out a
       * seat that has not started assigns a child access they will not have today.
       *
       * `includeScheduled` is the deliberate exception — a school planning next term's roster from
       * the seats a renewal already bought. The surface that asks for it has to SAY so, because the
       * child stays Terbatas until the pool opens.
       */
      options.includeScheduled ? undefined : lte(Premium.startDate, now),
      gt(Premium.endDate, now),
      isNull(Premium.childId),
      or(
        eq(Premium.membershipId, membershipId),
        addOnIds.length > 0 ? inArray(Premium.membershipAddOnId, addOnIds) : sql`false`,
      ),
    ),
    // `id` breaks ties so two concurrent grabs walk the same order — same reasoning as
    // `getAvailableOrgSeats`.
    orderBy: [asc(Premium.endDate), asc(Premium.id)],
    limit,
  })
}

/**
 * How many seats the organization owns that are LIVE TODAY. Cheap COUNT — no rows pulled back.
 *
 * Covering window on both ends, matching {@link countPremiumReach}: this is the family access gate
 * (see `resolvePremiumGate`), so a subscription prepaid to start next month must not read as access
 * today. Both gate metrics are bounded the same way on purpose — one bounded and one not is the
 * asymmetry that lets a prepaid pool unlock a feature early.
 */
export async function countActiveOrgSeats(db: Database, organizationId: string): Promise<number> {
  const now = new Date()
  const [row] = await db
    .select({ value: count() })
    .from(Premium)
    .where(and(eq(Premium.organizationId, organizationId), lte(Premium.startDate, now), gt(Premium.endDate, now)))
  return row?.value ?? 0
}

/**
 * Live seat counts for several organizations at once, keyed by organization id. Organizations with
 * no live seats are absent from the map rather than present with a zero.
 *
 * For list surfaces — the organization switcher shows a seat count per organization, so a per-row
 * query would be one round trip per membership the user holds.
 *
 * Covering window, the same bound {@link countActiveOrgSeats} and {@link countPremiumReach} apply.
 * This map decides the switcher's Premium marker and the plan word in the account menu, so without
 * the lower bound an organization that prepaid a pool starting next month reads Premium in both
 * while `resolvePremiumGate` — correctly — still walls its adults out of the reader.
 */
export async function countActiveSeatsByOrg(db: Database, organizationIds: string[]): Promise<Map<string, number>> {
  const map = new Map<string, number>()
  if (organizationIds.length === 0) return map

  const now = new Date()
  const rows = await db
    .select({ organizationId: Premium.organizationId, value: count() })
    .from(Premium)
    .where(and(inArray(Premium.organizationId, organizationIds), lte(Premium.startDate, now), gt(Premium.endDate, now)))
    .groupBy(Premium.organizationId)

  for (const row of rows) {
    if (row.organizationId) map.set(row.organizationId, Number(row.value))
  }
  return map
}

/**
 * {@link countPremiumReach} for several organizations at once, keyed by organization id.
 * Organizations reaching nobody are absent from the map rather than present with a zero.
 *
 * For the organization switcher, which marks each organization the caller belongs to as Premium or
 * not. That marker follows ACCESS, so it has to see a child seated by the OTHER organization the
 * child belongs to — a school seat marking the family Premium, and a parent's subscription marking
 * the school Premium. A per-row query would be one round trip per membership the caller holds.
 */
export async function countPremiumReachByOrg(db: Database, organizationIds: string[]): Promise<Map<string, number>> {
  const map = new Map<string, number>()
  if (organizationIds.length === 0) return map

  const now = new Date()
  const rows = await db
    .select({ organizationId: member.organizationId, value: countDistinct(member.userId) })
    .from(member)
    .innerJoin(user, and(eq(user.id, member.userId), eq(user.userType, 'child')))
    .innerJoin(Premium, and(eq(Premium.childId, member.userId), lte(Premium.startDate, now), gt(Premium.endDate, now)))
    .where(inArray(member.organizationId, organizationIds))
    .groupBy(member.organizationId)

  for (const row of rows) map.set(row.organizationId, Number(row.value))
  return map
}

/**
 * True when the organization holds at least one live COMMERCIAL pool.
 *
 * An organization whose seats are all grants reads as "belum premium": its children have exactly the
 * same access as anyone else's, but every billing call to action becomes "Jadi Premium" rather than
 * "Perpanjang". This answer never restricts access — it only decides copy.
 */
export async function isCommercialPremium(db: Database, organizationId: string): Promise<boolean> {
  const pools = await getOrgSeatPools(db, organizationId)
  return pools.some((pool) => pool.isCommercial && pool.seatsTotal > 0)
}

/**
 * How many children in this organization currently hold premium access, counting seats granted by
 * ANY organization — a parent's family subscription reaches the child through home-school-connect
 * and unlocks exactly what a school seat unlocks.
 *
 * Distinct children, not seat rows. The two are equal while the one-seat-per-child rule holds; this
 * counts children so a stray double seat cannot inflate a feature gate.
 *
 * Counts only seats COVERING today (`startDate <= now < endDate`), not merely unexpired ones. This
 * is the feature-gating number, so it has to answer "who can read premium right now" rather than
 * "who holds a seat, current or prepaid".
 */
export async function countPremiumReach(db: Database, organizationId: string): Promise<number> {
  const now = new Date()
  const [row] = await db
    .select({ value: countDistinct(member.userId) })
    .from(member)
    .innerJoin(user, and(eq(user.id, member.userId), eq(user.userType, 'child')))
    .innerJoin(
      Premium,
      // COVERING window, both ends — the same test `findCoveringSeatForChild` applies, and for the
      // same reason. Without the `startDate` lower bound a seat prepaid for next term counts as
      // reach today, so activating a grant dated for next term could open premium reading for the
      // whole staff room while the children those seats belong to still cannot open a book.
      and(eq(Premium.childId, member.userId), lte(Premium.startDate, now), gt(Premium.endDate, now)),
    )
    .where(eq(member.organizationId, organizationId))
  return row?.value ?? 0
}

/**
 * Children of this organization covered by a seat this organization did NOT provision — another
 * school's pool, or a parent's own family subscription with no organization at all.
 *
 * Half of {@link countPremiumCapacity}, and the half owned seats cannot supply: a seat a parent
 * pays for carries their family's `organizationId` (or none), so a count over this organization's
 * seat rows is blind to it. Excluding seats this organization owns is what stops the two halves
 * counting one child twice — the child is already inside the owned-seat total.
 *
 * `isNull` is part of the test, not a nicety: a seat with no organization at all is still not this
 * organization's, and `ne(col, value)` is false for NULL in SQL, so a family subscription would
 * drop out of the count entirely. Same reasoning as {@link getForeignSeatCoverage}.
 *
 * Covering window (`startDate <= now < endDate`), matching every other gate input.
 */
export async function countForeignPremiumReach(db: Database, organizationId: string): Promise<number> {
  const now = new Date()
  const [row] = await db
    .select({ value: countDistinct(member.userId) })
    .from(member)
    .innerJoin(user, and(eq(user.id, member.userId), eq(user.userType, 'child')))
    .innerJoin(Premium, and(eq(Premium.childId, member.userId), lte(Premium.startDate, now), gt(Premium.endDate, now)))
    .where(
      and(
        eq(member.organizationId, organizationId),
        or(isNull(Premium.organizationId), ne(Premium.organizationId, organizationId)),
      ),
    )
  return row?.value ?? 0
}

/**
 * CAPACITY — the number the school gates count against, and what the "x/50" locks display.
 *
 * `seats this organization owns` + `its children covered from elsewhere`:
 * - Every live seat in the organization's pools, ASSIGNED OR NOT ({@link countActiveOrgSeats}). A
 *   school handed 10 scholarship seats stands at 10 the moment they are activated. Counting only
 *   seated children read 1/50 the day after a 10-seat grant, which describes the school's roster
 *   admin work rather than its Premium standing, and gave a school no credit for what it holds.
 * - Plus children of this school seated by somebody else ({@link countForeignPremiumReach}) —
 *   parents subscribing on their own move the bar exactly as far as school-bought seats do.
 *
 * The two halves are disjoint by construction, so no child and no seat is counted twice.
 *
 * NOT REACH, and not a reading-activity number. It answers "how much Premium does this school
 * stand on", not "how many children can open a premium book right now" — an unassigned seat opens
 * nothing for nobody until it is given to a child. Where the question is access for one child, use
 * `findCoveringSeatForChild`; where it is how many children actually hold access, use
 * {@link countPremiumReach}.
 */
export async function countPremiumCapacity(db: Database, organizationId: string): Promise<number> {
  const [ownedSeats, foreignReach] = await Promise.all([
    countActiveOrgSeats(db, organizationId),
    countForeignPremiumReach(db, organizationId),
  ])
  return ownedSeats + foreignReach
}

/**
 * The child's live seat, wherever it came from — this organization, another one, or a family
 * subscription with no organization at all.
 *
 * This is the SEAT-GRANT guard, and its looseness is the point: no lower bound on `startDate`, so a
 * seat prepaid for next term still blocks a second grant today. Scoping the check to one organization
 * is what let a school burn a seat on a child a parent had already covered, and left the child holding
 * two seats — which royalty then counted twice, since the payout pool is `count(distinct premium_id)`.
 *
 * It is NOT "is this child premium right now": the constraint behind it (`no_overlapping_child_seats`,
 * an EXCLUDE over overlapping windows) permits a current seat and a disjoint future one at once, so
 * this can answer with a seat covering nothing today. For access and for billing a reading session,
 * use `findCoveringSeatForChild`.
 */
export async function findActiveSeatForChild(db: Database, childId: string): Promise<SelectPremium | undefined> {
  const now = new Date()
  return db.query.Premium.findFirst({
    where: and(eq(Premium.childId, childId), gt(Premium.endDate, now)),
    orderBy: [desc(Premium.endDate), asc(Premium.id)],
  })
}

/**
 * The seat the child is reading on RIGHT NOW — `startDate <= now < endDate`.
 *
 * Distinct from `findActiveSeatForChild`, which has no lower bound and so answers with a seat bought
 * for next term. That looseness is correct where it is used: before spending a seat, "does this child
 * already hold one, current or prepaid" is exactly the question. It is wrong for two others, which
 * are the callers here — whether the child may open a premium book today, and which seat a reading
 * session is charged to. A child can legitimately hold a current seat and a prepaid renewal at once,
 * and answering either question with the renewal walls a covered child or bills the wrong seat; the
 * royalty pool is `count(distinct premium_id)`, so a misattributed session pays out against a seat
 * nobody read on.
 *
 * Soonest-ending first, `id` breaking ties: of two seats covering today, the one closing sooner is
 * the one being consumed, and a stable order keeps one child's sessions on one seat.
 */
export async function findCoveringSeatForChild(db: Database, childId: string): Promise<SelectPremium | undefined> {
  const now = new Date()
  return db.query.Premium.findFirst({
    where: and(eq(Premium.childId, childId), lte(Premium.startDate, now), gt(Premium.endDate, now)),
    orderBy: [asc(Premium.endDate), asc(Premium.id)],
  })
}

/** The ids of those children in `childIds` who already hold a live seat anywhere. */
export async function findChildrenWithActiveSeat(
  db: Database,
  childIds: string[],
  /**
   * The window a seat would be granted for. Defaults to "from now on", which is the live-handout
   * question: does this child already hold a seat?
   *
   * Pass a pool's own window when assigning seats for a FUTURE term. The children a school wants to
   * seat there are exactly the ones sitting in the current pool, so the default test would report
   * every one of them as already seated and leave next term's roster empty. A seat ending before
   * the new window opens does not overlap it, and `no_overlapping_child_seats` agrees — the bounds
   * here are the half-open ones that constraint enforces.
   */
  window?: { startDate: Date; endDate: Date },
): Promise<Set<string>> {
  if (childIds.length === 0) return new Set()
  const from = window?.startDate ?? new Date()
  const rows = await db
    .select({ childId: Premium.childId })
    .from(Premium)
    .where(
      and(
        inArray(Premium.childId, childIds),
        gt(Premium.endDate, from),
        window ? lt(Premium.startDate, window.endDate) : undefined,
      ),
    )
  return new Set(rows.map((r) => r.childId).filter((id) => id !== null))
}

export interface SchoolEntitlement extends SelectSeatEntitlement {
  seatsAllocated: number
  seatsRemaining: number
}

/**
 * Seat entitlements assigned to a school, each with computed `seatsAllocated` / `seatsRemaining`
 * (allocated = count of Premium rows tagged with the entitlement id).
 */
export async function getSchoolEntitlements(
  db: Database,
  params: { organizationId: string; status?: SeatEntitlementStatus },
): Promise<SchoolEntitlement[]> {
  const entitlements = await db.query.SeatEntitlement.findMany({
    where: and(
      eq(SeatEntitlement.assignedSchoolId, params.organizationId),
      params.status ? eq(SeatEntitlement.status, params.status) : undefined,
    ),
    orderBy: [desc(SeatEntitlement.createdAt)],
  })

  const ids = entitlements.map((e) => e.id)
  if (ids.length === 0) return []

  // "Allocated" = seats actually handed to a child (childId set). Seats are provisioned as an
  // unassigned pool at activation, so counting rows alone would report a full entitlement before
  // anyone is assigned.
  const counts = await db
    .select({ entitlementId: Premium.entitlementId, allocated: count() })
    .from(Premium)
    .where(and(inArray(Premium.entitlementId, ids), isNotNull(Premium.childId)))
    .groupBy(Premium.entitlementId)

  const allocatedById = new Map<string, number>()
  for (const c of counts) {
    if (c.entitlementId) allocatedById.set(c.entitlementId, Number(c.allocated))
  }

  return entitlements.map((e) => {
    const seatsAllocated = allocatedById.get(e.id) ?? 0
    return { ...e, seatsAllocated, seatsRemaining: e.totalSeats - seatsAllocated }
  })
}

/**
 * Which children currently sit in ONE pool, in a stable order.
 *
 * The renewal picker's input: a renewal continues the students the school chooses, so the form has
 * to list exactly who is seated on the pool being renewed. Scoped by `organizationId` as well as by
 * pool, so a membership id belonging to another organization yields nothing rather than its roster.
 *
 * A pool's seats hang off two columns — the membership's own and its add-ons' — so both are read;
 * scoping by `membershipId` alone would drop every student sitting on an add-on seat, and a renewal
 * would quietly leave them behind.
 */
export async function getPoolAssignedChildIds(
  db: Database,
  { organizationId, membershipId }: { organizationId: string; membershipId: string },
): Promise<string[]> {
  const addOns = await db
    .select({ id: MembershipAddOn.id })
    .from(MembershipAddOn)
    .where(eq(MembershipAddOn.membershipId, membershipId))

  const addOnIds = addOns.map((row) => row.id)

  const rows = await db
    .select({ childId: Premium.childId })
    .from(Premium)
    .where(
      and(
        eq(Premium.organizationId, organizationId),
        isNotNull(Premium.childId),
        or(
          eq(Premium.membershipId, membershipId),
          addOnIds.length > 0 ? inArray(Premium.membershipAddOnId, addOnIds) : sql`false`,
        ),
      ),
    )
    .orderBy(asc(Premium.childId))

  return rows.map((row) => row.childId).filter((id): id is string => id !== null)
}

/**
 * Of `childIds`, which already hold cover that runs PAST `after` from somewhere other than the pool
 * ending there — a parent's own subscription, or another school's seat.
 *
 * The renewal picker's lock list. One child may not hold two seats whose windows overlap, so a
 * student already covered for the next term cannot be continued onto it: the checkout drops them
 * and the roster would show a seat the school paid for and cannot use. Naming them before the
 * purchase is the difference between "these two are already covered at home" and a failed order.
 *
 * `after` is the anchor pool's end date, which is where the renewed window begins. Any seat
 * reaching past it overlaps that window whatever term the buyer picks, so this answer does not
 * depend on a duration the form has not committed to yet.
 *
 * This organization's own free-trial seats are excluded — fulfilment releases those — and so is
 * every seat belonging to the pool being renewed, which by definition ends exactly at `after`.
 */
export async function findChildrenCoveredAfter(
  db: Database,
  {
    childIds,
    after,
    organizationId,
    membershipId,
  }: { childIds: string[]; after: Date; organizationId: string; membershipId: string },
): Promise<string[]> {
  if (childIds.length === 0) return []

  const addOns = await db
    .select({ id: MembershipAddOn.id })
    .from(MembershipAddOn)
    .where(eq(MembershipAddOn.membershipId, membershipId))

  const addOnIds = addOns.map((row) => row.id)

  const rows = await db
    .select({ childId: Premium.childId })
    .from(Premium)
    .leftJoin(SeatEntitlement, eq(SeatEntitlement.id, Premium.entitlementId))
    .where(
      and(
        inArray(Premium.childId, childIds),
        gt(Premium.endDate, after),
        or(
          isNull(SeatEntitlement.sourceType),
          ne(SeatEntitlement.sourceType, 'free_trial'),
          ne(Premium.organizationId, organizationId),
        ),
        /**
         * Not a seat of the pool being renewed. Those end at `after`, so they never reach past it —
         * this only matters for a pool whose seats were individually extended.
         *
         * Written as `IS NULL OR <>` rather than `NOT (... = ...)`: a seat carries EITHER
         * `membershipId` or `membershipAddOnId`, so one of the two is always NULL, and `NOT (NULL =
         * x)` is NULL — which drops the row. Under a bare `NOT`, every add-on seat would silently
         * count as unblocked.
         */
        or(isNull(Premium.membershipId), ne(Premium.membershipId, membershipId)),
        addOnIds.length > 0
          ? or(isNull(Premium.membershipAddOnId), notInArray(Premium.membershipAddOnId, addOnIds))
          : undefined,
      ),
    )

  return [...new Set(rows.map((row) => row.childId).filter((id): id is string => id !== null))]
}

/**
 * Is this organization a school? `organization.type` is a text array, so a single organization can
 * be both a school and a family.
 *
 * Load-bearing for the premium gates below: a school unlocks on CAPACITY (seats it holds, plus its
 * children seated elsewhere), a family on owned seats OR reach. Applying the school rule to a family
 * would wall a parent who has paid, because a household of two can never reach eight.
 */
export async function isSchoolOrganization(
  db: Database | DatabaseTransaction,
  organizationId: string,
): Promise<boolean> {
  const [row] = await db
    .select({ isSchool: sql<boolean>`'school' = ANY(${organization.type})` })
    .from(organization)
    .where(eq(organization.id, organizationId))
    .limit(1)
  return row?.isSchool ?? false
}

/** What the premium gates need about one organization. A school and a family are gated on
 * different numbers, so this is a union rather than one shape carrying both — a caller cannot read
 * a field that does not apply to the organization in front of it. */
export type OrganizationPremiumStanding =
  | {
      isSchool: true
      /** {@link countPremiumCapacity} — owned seats, assigned or not, plus foreign-covered children. */
      capacity: number
    }
  | {
      isSchool: false
      /** {@link countPremiumReach} — children here holding a live seat from any source. */
      reach: number
      /** {@link countActiveOrgSeats} — live seats this organization itself provisioned. */
      ownedSeats: number
    }

/**
 * Everything the premium gates need about one organization, in one round trip.
 *
 * A SCHOOL is decided entirely by CAPACITY: seats it owns (assigned or not) plus children seated by
 * someone else. Unassigned seats count because a school that has been handed 10 scholarship seats
 * holds 10 seats' worth of Premium whether or not an admin has finished handing them out; a gate
 * that waited for the roster work read 1/50 on the day a 10-seat grant landed.
 *
 * A FAMILY needs BOTH `reach` and `ownedSeats`, and each on its own is wrong:
 * - Owned seats alone wall a parent whose child was seated by their school through
 *   Sambung Sekolah-Rumah. The seat is real and the child is reading on it; it simply carries the
 *   school's `organizationId`, so an owned-seat count cannot see it.
 * - Reach alone walls a parent who has just bought a subscription and not yet handed the seat to a
 *   child. They have paid; reach counts seated children, so it reads zero.
 * The family rule is therefore `ownedSeats > 0 || reach > 0` — see `resolvePremiumGate`.
 *
 * A family is NOT gated on capacity: capacity counts seats, and one unassigned seat is exactly the
 * case the family rule already covers through `ownedSeats`.
 */
export async function getOrganizationPremiumStanding(
  db: Database,
  organizationId: string,
): Promise<OrganizationPremiumStanding> {
  const isSchool = await isSchoolOrganization(db, organizationId)

  if (isSchool) {
    return { isSchool: true, capacity: await countPremiumCapacity(db, organizationId) }
  }

  const [reach, ownedSeats] = await Promise.all([
    countPremiumReach(db, organizationId),
    countActiveOrgSeats(db, organizationId),
  ])
  return { isSchool: false, reach, ownedSeats }
}

/** One organization covering some of this organization's children with seats it provisioned. */
export interface ForeignSeatCoverage {
  /** The organization that provisioned the seats. Null for a seat with no organization at all. */
  organizationId: string | null
  /** Its name, for copy like "Akses Premium dari sekolah X". Null when unnamed or org-less. */
  organizationName: string | null
  /** Whether that organization is a school — the copy says "sekolah" only when it is. */
  isSchool: boolean
  /** How many of THIS organization's children it covers. */
  childCount: number
  /** The soonest end date among those seats: when this organization starts losing the access. */
  endsAt: Date
}

/**
 * Premium access this organization's children have that this organization did NOT pay for.
 *
 * The read behind "Akses Premium dari sekolah X sampai <tanggal>" on a family's billing page, and
 * behind the warning that the access is about to lapse. A family whose child was seated by their
 * school reads as Premium everywhere access is asked about, while its own pool list is empty — which
 * without this query leaves a screen saying "Starter" and no explanation of why the child can read.
 *
 * NOT A BILLING NUMBER, and it must never become one. These seats belong to another organization's
 * pool: they cannot be renewed here, they are not part of Tersedia/Terpakai, they carry no invoice,
 * and the reminder cron must keep ignoring them. Grouped per source organization with the SOONEST
 * end date, for the same reason pools are listed rather than summed — a single date would hide
 * whichever access lapses first.
 *
 * Covering window (`startDate <= now < endDate`), matching {@link countPremiumReach}: this explains
 * access the child has TODAY, so a seat the school has prepaid for next term must not be described
 * as current coverage.
 */
export async function getForeignSeatCoverage(db: Database, organizationId: string): Promise<ForeignSeatCoverage[]> {
  const now = new Date()
  const sourceOrg = alias(organization, 'source_organization')

  const rows = await db
    .select({
      organizationId: Premium.organizationId,
      organizationName: sourceOrg.name,
      isSchool: sql<boolean>`coalesce('school' = ANY(${sourceOrg.type}), false)`,
      childCount: countDistinct(member.userId),
      endsAt: sql<Date>`min(${Premium.endDate})`,
    })
    .from(member)
    .innerJoin(user, and(eq(user.id, member.userId), eq(user.userType, 'child')))
    .innerJoin(Premium, and(eq(Premium.childId, member.userId), lte(Premium.startDate, now), gt(Premium.endDate, now)))
    // Provisioned by someone else. `isNull` is part of the test, not a nicety: a seat with no
    // organization at all is still not this organization's, and `ne(col, value)` is false for NULL
    // in SQL, so the null case has to be spelled out or such a seat vanishes from the explanation.
    .leftJoin(sourceOrg, eq(sourceOrg.id, Premium.organizationId))
    .where(
      and(
        eq(member.organizationId, organizationId),
        or(isNull(Premium.organizationId), sql`${Premium.organizationId} <> ${organizationId}`),
      ),
    )
    .groupBy(Premium.organizationId, sourceOrg.name, sourceOrg.type)
    .orderBy(asc(sql`min(${Premium.endDate})`))

  return rows.map((row) => ({
    organizationId: row.organizationId,
    organizationName: row.organizationName,
    isSchool: Boolean(row.isSchool),
    childCount: Number(row.childCount),
    endsAt: new Date(row.endsAt),
  }))
}
